import throttle from 'lodash/throttle';
import { scrollTo } from 'Models/scroll';
import { dim, undim } from 'Models/dimmer';
import { lockPage, unlockPage } from 'Models/locker';
import store from 'Store';
import { dispatch, listen } from 'Models/utils/event-bus';
import generateButton from 'Models/button';
import generateIcon from 'Models/icon';
import fetchData from 'Models/fetch-data';
import { showPreloader, hidePreloader } from 'Models/preloader';
import SimpleScrollbar from 'simple-scrollbar';
import * as ProjectConfig from 'Models/utils/project-config';
import Cookies from 'js-cookie';
import { parse } from 'Models/utils/App';
import { lowerFirst } from 'lodash';
import initSentry from 'Models/init-sentry';

export default function header (element) {
    const elements = {
        showSearch: element.querySelector('.js-header__show-search'),
        hideSearch: element.querySelector('.js-header__hide-search'),
        toggleMobileSearch: element.querySelector(
            '.js-header__toggle-mobile-search',
        ),
        toggleMobileNav: element.querySelector('.js-header__toggle-mobile-nav'),
        rootLinksWithSubnav: element.querySelectorAll(
            '.js-header__root-link-with-subnav',
        ),
        scrollLink: element.querySelectorAll('.js-header__scroll-link'),
        rootLink: element.querySelectorAll('.js-header__root-link'),
        toggleFlyout: element.querySelectorAll('.js-header__toggle-flyout'),
        box: element.querySelector('.js-header__box'),
        searchInput: element.querySelector('.js-header__search-input'),
        flyoutContainer: document.querySelectorAll('.js-flyouts__scroll'),
        toggleSearchOptions: element.querySelector('.js-header__toggle-search-options'),
        toggleSearchOptionsSelected: element.querySelector('.js-header__toggle-search-options-selected'),
        searchOptionsItems: element.querySelectorAll('.js-header__search-options-item'),
        languageLinks: element.querySelectorAll('.js-header__language-link'),
        search: document.querySelector('.js-header__search'),
    };

    const states = {
        isExpanded: 'is-expanded',
        isSearch: 'is-search',
        isSearchManual: 'is-search-manual',
        isSearchFilled: 'is-search-filled',
        isMobileMenu: 'is-mobile-menu',
        isMobileNav: 'is-mobile-nav',
        isMobileSearch: 'is-mobile-search',
        isSubnavExpanded: 'is-subnav-expanded',
        isLinkActive: 'is-link-active',
        isNoNav: 'is-no-nav',
        isFlyoutOpen: 'is-flyout-open',
        isLoggedIn: 'is-logged-in',
        isShopNavigation: 'is-shop-navigation',
    };

    const scrollBehavior = element.dataset.scrollBehavior;
    const loginContainer = element.querySelector('.js-login');
    const swichesContainer = element.querySelector('.js-header__mobile-icons');
    const flyoutsContainer = element.querySelector('.js-flyouts__container');

    // check local storage to see if user is logged in
    const loggedInCookieName = 'recaUserLoggedIn';
    let loggedInData = Cookies.get(loggedInCookieName);
    let loggedIn = loggedInData !== undefined;
    if (loggedInData !== undefined) {
        loggedInData = JSON.parse(loggedInData);
        loggedIn = loggedInData.konto.angemeldet;
    }

    // endpoints
    let loginJson = false;
    let kontoJson = false;
    let bestellJson = false;
    let warenkorbJson = false;
    let loginLabel = false;
    let errorMsg = false;
    if (loginContainer) {
        loginJson = loginContainer.dataset.loginJson;
        kontoJson = loginContainer.dataset.kontoJson;
        bestellJson = loginContainer.dataset.bestellJson;
        warenkorbJson = loginContainer.dataset.warenkorbJson;

        // logged-off label
        loginLabel = loginContainer.dataset.loginLabel;
        errorMsg = loginContainer.dataset.errorMsg;
    }

    let scrollSectionPositions = [];
    let baseLink;
    let listenToScroll = true;
    let scrollBehaviorScrolled = false;
    let previousScrollValue = 0;
    let pageLocked = false;
    let scrollTimeout;
    const activeSectionProportion = 0.4;
    let navigationAnimating = false;
    let hideNavPositionRefreshTimeout;

    let cartLink;
    let dashboardLink;

    let searchOption = 'all';
    const redirectOnEnter = elements.searchInput && elements.searchInput.dataset.redirectOnEnter === 'true';

    const isShopNavigation = element.classList.contains(states.isShopNavigation);
    const shopId = element.dataset.shopId ? element.dataset.shopId : false;

    function init () {
        initSentry();
        if (loginJson) {
            const wasLoggedOut = checkForLogout();
            showLoginStatus(loggedIn);
            if (!wasLoggedOut && !loggedIn) {
                checkLoginAsync();
            }
        }

        addListeners();
        initScrollLinkLogic();

        if (isShopNavigation) {
            getLanguageLinksFromHreflang();
        }
    }

    function addListeners () {
        elements.toggleMobileNav.addEventListener(
            'click',
            toggleMobileNavClickHandler,
        );
        if (elements.toggleMobileSearch) {
            elements.toggleMobileSearch.addEventListener(
                'click',
                toggleMobileSearchClickHandler,
            );
        }
        if (elements.showSearch) {
            elements.showSearch.addEventListener(
                'click',
                showSearchClickHandler,
            );
        }
        if (elements.hideSearch) {
            elements.hideSearch.addEventListener(
                'click',
                hideSearchClickHandler,
            );
        }
        if (elements.box) {
            elements.box.addEventListener('click', boxClickHandler);
        }
        if (elements.toggleFlyout) {
            elements.toggleFlyout.forEach(toggleFlyoutItem => {
                toggleFlyoutItem.addEventListener(
                    'click',
                    toggleFlyoutItemClickHandler,
                );
            });
        }
        if (elements.rootLinksWithSubnav) {
            elements.rootLinksWithSubnav.forEach(rootLinkWithSubnav => {
                rootLinkWithSubnav.addEventListener(
                    'click',
                    rootLinkWithSubnavClickHandler,
                );
            });
        }
        if (elements.languageLinks) {
            elements.languageLinks.forEach(languageLink => {
                languageLink.addEventListener('click', languageLinkClickHandler);
            });
        }
        if (scrollBehavior) {
            checkInitialSearchVisibility();
            window.addEventListener(
                'scroll',
                throttle(() => {
                    handleScrollBehavior();
                }, 200),
            );
        }

        listen('flyout-opened', hideMobileMenuAndNav);

        store.watch(
            state => state.lockPage,
            lockPage => {
                if (lockPage) {
                    pageLocked = true;
                } else {
                    requestAnimationFrame(() => {
                        pageLocked = false;
                    });
                }
            },
            { immediate: false },
        );

        window.addEventListener(
            'header-notification-count-update',
            updateBubble,
        );

        // search
        if (elements.searchInput) {
            // check for query value in URL (for shop redirection)
            const urlSearchParams = new URLSearchParams(window.location.search);
            if (urlSearchParams.has('query')) {
                elements.searchInput.value = urlSearchParams.get('query');
            }
            // set default search option if present
            // if (elements.searchOptionsItems && elements.searchOptionsItems[0]) {
            //     // searchOption = elements.searchOptionsItems[0].dataset.value;
            // }

            elements.searchInput.addEventListener('focus', function () {
                if (elements.searchInput.value !== '' && searchOption === 'all') {
                    store.commit('setSearchSuggestionsCurrentlyVisible', true);
                }
            });

            elements.searchInput.addEventListener('input', function () {
                if (searchOption === 'all') {
                    const query = elements.searchInput.value;
                    toggleSearchResetButton(query);
                    dispatch('search-query', query);
                } else {
                    store.commit('setSearchQuery', elements.searchInput.value);
                }
            });

            elements.searchInput.addEventListener('keypress', function (e) {
                if (e.key === 'Enter') {
                    if (redirectOnEnter) {
                        let redirectUrl = ProjectConfig.get('factfinder.redirectUrl');
                        const query = elements.searchInput.value;
                        redirectUrl = redirectUrl.replace('QUERY', encodeURIComponent(query));
                        setActiveSearchOption();
                        window.location.href = `${redirectUrl}${searchOption !== 'all' ? `${redirectUrl.includes('?') ? '&' : '?'}${searchOption}=true` : ''}`;
                    }
                }
            });

            listen('toggle-search-mobile', toggleMobileSearch);
            listen('open-search-desktop', function () {
                toggleSearchResetButton(elements.searchInput.value);
                showSearch(
                    scrollBehavior !== 'hide-search' &&
                        scrollBehavior !== 'hide-nav',
                );
            });
        }
        if (elements.toggleSearchOptions) {
            elements.toggleSearchOptions.addEventListener('click', toggleSearchOptionsFlyout);
            document.addEventListener('click', clickOutsideSearchOptionsFlyout);
        }
        if (elements.searchOptionsItems) {
            elements.searchOptionsItems.forEach(item => {
                item.addEventListener('click', updateSearchOptionSelection);
            });
        }
    }

    function initScrollLinkLogic () {
        if (elements.scrollLink && checkAndProcessScrollSections()) {
            baseLink = element.querySelector('.' + states.isLinkActive);
            highlightScrollLink();
            elements.scrollLink.forEach(scrollLink => {
                scrollLink.addEventListener('click', scrollLinkClickHandler);
            });

            setTimeout(() => {
                checkAndProcessScrollSections(); // recheck the heights after elements are loaded
            }, 2000);
            window.addEventListener(
                'scroll',
                throttle(() => {
                    highlightScrollLink();
                    checkForBottom();
                }, 200),
            );
            store.watch(
                state => state.windowWidth,
                () => {
                    checkAndProcessScrollSections();
                },
                { immediate: false },
            );
        }
    }

    function toggleMobileNavClickHandler () {
        toggleMobileNav();
    }

    function toggleMobileSearchClickHandler () {
        toggleMobileSearch();
    }

    function showSearchClickHandler () {
        showSearch(true);
    }

    function hideSearchClickHandler () {
        resetSearch();
        undim();
        if (
            !scrollBehavior ||
            (scrollBehavior === 'hide-search' && window.pageYOffset > 0)
        ) {
            hideSearch();
        }
    }

    function rootLinkWithSubnavClickHandler (e) {
        if (store.getters.getActiveBreakpoints.indexOf('tablet-landscape') < 0 || document.body.classList.contains('touch')) e.preventDefault();
        const navItemElement = e.currentTarget.parentElement;
        toggleSubnav(navItemElement);
    }

    function toggleFlyoutItemClickHandler (e) {
        const flyoutName = e.currentTarget.dataset.toggleFlyout;
        resetSearch();
        toggleFlyout(flyoutName);

        if (kontoJson || bestellJson || bestellJson) {
            if (store.getters.getActiveFlyout === null) {
                return false;
            }
            switch (flyoutName) {
            case 'user':
                if (!kontoJson) {
                    break;
                } else {
                    loadProfile();
                }
                break;

            case 'dashboard':
                if (!bestellJson) {
                    break;
                } else {
                    loadDashboard();
                }
                break;

            case 'cart':
                if (!warenkorbJson) {
                    break;
                } else {
                    loadCart();
                }
                break;

            default:
                break;
            }
        }
    }

    function boxClickHandler (e) {
        if (e.target === e.currentTarget) {
            closeMenuAfterClickingOutside();
        }
    }

    function scrollLinkClickHandler (e) {
        const navLinkElement = e.currentTarget;
        listenToScroll = true;
        hideMobileMenuAndNav();
        highlightScrollLink(navLinkElement.parentElement);
        scrollToSection(navLinkElement, e);
        listenToScroll = false;
        if (scrollTimeout !== undefined) {
            clearTimeout(scrollTimeout);
        }
        scrollTimeout = setTimeout(() => {
            listenToScroll = true;
            if (scrollSectionReached(navLinkElement)) {
                highlightScrollLink();
            }
        }, 1500);
    }

    function languageLinkClickHandler (e) {
        const link = e.currentTarget;
        const hreflang = link.getAttribute('hreflang');
        if (shopId && hreflang) {
            e.preventDefault();
            Cookies.set(`${shopId}_LANG`, hreflang);
            window.location.href = link.getAttribute('href');
        }
    }

    function showSearch (manual = false) {
        // elements.search.setAttribute('data-component', '{"factory": "vue"}');
        // setTimeout(() => {
        //     parse(element.parentElement);
        // }, 0);

        // elements.search
        const query = elements.searchInput.value;
        expandHeader();
        element.classList.add(states.isSearch);

        toggleSearchResetButton(query);
        store.commit('setSearchBarVisibility', true);
        if (manual) {
            element.classList.add(states.isSearchManual);
            setTimeout(() => {
                focusOnSearchField();
            }, 800);
        }
    }

    function hideSearch () {
        compressHeader();
        element.classList.remove(states.isSearch);
        element.classList.remove(states.isSearchManual);
        element.classList.remove(states.isSearchFilled);
        store.commit('setSearchBarVisibility', false);
    }

    function resetSearch () {
        if (elements.searchInput) {
            element.classList.remove(states.isSearchFilled);
            elements.searchInput.value = '';
            dispatch('search-query', '');
        }
    }

    function showNav () {
        expandHeader();
        element.classList.remove(states.isNoNav);
    }

    function hideNav () {
        compressHeader();
        element.classList.add(states.isNoNav);
    }

    function expandHeader () {
        element.classList.add(states.isExpanded);
    }

    function compressHeader () {
        element.classList.remove(states.isExpanded);
    }

    function toggleMobileNav () {
        if (element.classList.contains(states.isMobileNav)) {
            hideMobileMenu();
            hideMobileNav();
        } else {
            showMobileMenu();
            showMobileNav();
        }
    }

    function hideMobileMenuAndNav () {
        if (element.classList.contains(states.isMobileNav)) {
            hideMobileMenu();
            hideMobileNav();
        }
    }

    function toggleMobileSearch () {
        if (element.classList.contains(states.isMobileSearch)) {
            hideMobileMenu();
            hideMobileSearch();
        } else {
            showMobileMenu();
            showMobileSearch();
            store.commit('setSearchBarVisibility', true);
        }
    }

    function showMobileMenu () {
        dim(true);
        lockPage();
        element.classList.add(states.isMobileMenu);
    }

    function hideMobileMenu () {
        undim();
        unlockPage();
        element.classList.remove(states.isMobileMenu);
    }

    function showMobileNav () {
        if (element.classList.contains(states.isMobileSearch) || element.classList.contains(states.isSearch)) {
            hideMobileSearch();
        }
        elements.box.scrollTo(0, 0);
        element.classList.add(states.isMobileNav);
    }

    function hideMobileNav () {
        element.classList.remove(states.isMobileNav);
    }

    function showMobileSearch () {
        if (element.classList.contains(states.isMobileNav)) {
            hideMobileNav();
        }
        elements.box.scrollTo(0, 0);
        element.classList.add(states.isMobileSearch);
        setTimeout(() => {
            focusOnSearchField();
        }, 800);
    }

    function hideMobileSearch () {
        element.classList.remove(states.isMobileSearch);
        elements.searchInput.value = '';
        dispatch('search-query', '');
        store.commit('setSearchBarVisibility', false);
    }

    function hideAllMobileMenus () {
        hideMobileMenu();
        if (element.classList.contains(states.isMobileNav)) {
            hideMobileNav();
        }
        if (element.classList.contains(states.isMobileSearch)) {
            hideMobileSearch();
        }
    }

    function toggleSubnav (navItemElement) {
        if (navItemElement.classList.contains(states.isSubnavExpanded)) {
            navItemElement.classList.remove(states.isSubnavExpanded);
        } else {
            navItemElement.classList.add(states.isSubnavExpanded);
        }
    }

    function scrollToSection (scrollLink, e) {
        const targetElement = getScrollTargetElementByLink(scrollLink);
        if (targetElement) {
            e.preventDefault();
            scrollTo(targetElement);
        }
    }

    function checkAndProcessScrollSections () {
        let sectionPresent = false;
        scrollSectionPositions = [];
        const scrollLinkCount = elements.scrollLink.length;
        for (let index = 0; index < scrollLinkCount; index++) {
            const scrollLink = elements.scrollLink[index];
            const targetSelector = getScrollLinkHash(scrollLink);
            const targetElement = document.querySelector(targetSelector);
            if (targetElement) {
                const targetElementPosition =
                    targetElement.getBoundingClientRect().top +
                    window.pageYOffset;
                scrollSectionPositions.push({
                    selector: targetSelector,
                    position: targetElementPosition,
                    link: scrollLink,
                });
                sectionPresent = true;
            }
        }
        if (sectionPresent) {
            scrollSectionPositions.sort(function (a, b) {
                return a.position - b.position;
            });
        }
        return sectionPresent;
    }

    function getScrollLinkHash (scrollLink) {
        let selector = scrollLink.getAttribute('href');
        if (!selector) {
            return false;
        }
        if (selector.indexOf('#')) {
            // selector = '#' + selector.split('#')[1];
            selector = `[data-id="${selector.split('#')[1]}"]`;
        }
        return selector;
    }

    function highlightScrollLink (navItem = false) {
        if (!listenToScroll || pageLocked) {
            return false;
        }
        if (!navItem) {
            navItem = getNavElementOfSectionInView();
        }
        if (!navItem) {
            return false;
        }
        if (!navItem.classList.contains(states.isLinkActive)) {
            const oldLink = element.querySelector('.' + states.isLinkActive);
            if (oldLink) oldLink.classList.remove(states.isLinkActive);
            navItem.classList.add(states.isLinkActive);
        }
    }

    function getNavElementOfSectionInView () {
        const windowPosition =
            document.documentElement.scrollTop +
            window.innerHeight * activeSectionProportion;
        const scrollSectionPositionsCount = scrollSectionPositions.length;
        for (var i = scrollSectionPositionsCount - 1; i >= 0; --i) {
            if (scrollSectionPositions[i].position < windowPosition) {
                return scrollSectionPositions[i].link.parentElement;
            }
        }
        return baseLink;
    }

    function handleScrollBehavior () {
        if (scrollBehavior === 'hide-search') {
            handleHideSearchScrollBehavior();
        }
        if (scrollBehavior === 'hide-nav') {
            handleHideNavScrollBehavior();
        }
    }

    function handleHideSearchScrollBehavior () {
        if (window.pageYOffset > 0) {
            if (scrollBehaviorScrolled) {
                return false;
            }
            hideSearch();
            scrollBehaviorScrolled = true;
        } else {
            if (!scrollBehaviorScrolled) {
                return false;
            }
            showSearch();
            scrollBehaviorScrolled = false;
        }
    }

    function handleHideNavScrollBehavior () {
        if (pageLocked) return false;
        const scrollDifference =
            parseInt(window.pageYOffset) - previousScrollValue;
        let scrollDir = false;
        if (scrollDifference <= 0 || window.pageYOffset <= 0) {
            scrollDir = 'up';
        } else if (scrollDifference > 0) {
            scrollDir = 'down';
        }

        if (navigationAnimating || !scrollDir) {
            return false;
        }
        if (!navigationAnimating) {
            previousScrollValue = parseInt(window.pageYOffset);
            if (hideNavPositionRefreshTimeout) {
                clearTimeout(hideNavPositionRefreshTimeout);
            }
        }
        let actionPerformed = false;

        if (!scrollBehaviorScrolled && scrollDir === 'down') {
            hideNav();
            scrollBehaviorScrolled = true;
            actionPerformed = true;
        } else if (scrollBehaviorScrolled && scrollDir === 'up') {
            showNav();
            actionPerformed = true;
            scrollBehaviorScrolled = false;
        }

        if (actionPerformed) {
            navigationAnimating = !!scrollDir;
            setTimeout(() => {
                navigationAnimating = false;
            }, 550);
            hideNavPositionRefreshTimeout = setTimeout(() => {
                previousScrollValue = parseInt(window.pageYOffset);
            }, 550);
        }
    }

    function toggleFlyout (flyoutName) {
        const setName =
            store.getters.getActiveFlyout === flyoutName ? null : flyoutName;
        store.commit('setActiveFlyout', setName);
    }

    function closeMenuAfterClickingOutside () {
        if (store.getters.getActiveFlyout) {
            return false;
        }
        hideAllMobileMenus();
    }

    function focusOnSearchField () {
        // if (store.getters.getBrowser !== 'safari') {
        //     elements.searchInput.focus();
        // }
    }

    function checkInitialSearchVisibility () {
        if (scrollBehavior === 'hide-search' || scrollBehavior === 'hide-nav') {
            // search is initially visible
            store.commit('setSearchBarVisibility', true);
        }
    }

    function toggleSearchResetButton (query) {
        if (query.length) {
            element.classList.add(states.isSearchFilled);
        } else {
            element.classList.remove(states.isSearchFilled);
        }
    }

    function getScrollTargetElementByLink (scrollLink) {
        const targetSelector = getScrollLinkHash(scrollLink);
        return document.querySelector(targetSelector);
    }

    function scrollSectionReached (scrollLink) {
        const targetElement = getScrollTargetElementByLink(scrollLink);
        return (
            targetElement.getBoundingClientRect().top <=
            window.innerHeight * activeSectionProportion
        );
    }

    function checkForBottom () {
        const reachedBottom =
            document.querySelector('.page').getBoundingClientRect().bottom <=
            window.innerHeight + 10;
        if (reachedBottom) {
            highlightLastScrollLink();
        }
    }

    function highlightLastScrollLink () {
        const lastScrollLink = elements.scrollLink.item(
            elements.scrollLink.length - 1,
        ).parentElement;
        highlightScrollLink(lastScrollLink);
    }

    function setActiveSearchOption () {
        if (!elements.toggleSearchOptionsSelected) return false;
        let existingSearchOption = localStorage.getItem('recaSelectedSearchOption');
        if (existingSearchOption && existingSearchOption !== '' && loggedIn) {
            existingSearchOption = JSON.parse(existingSearchOption);
            searchOption = existingSearchOption.value;
            elements.toggleSearchOptionsSelected.innerHTML = existingSearchOption.label;
        }
    }

    async function loadProfile () {
        const profileContainer = element.querySelector('.js-profil__container');
        const profileInner = element
            .querySelector('.js-profil__inner')
            .cloneNode();
        profileContainer.innerHTML = '';
        profileContainer.appendChild(profileInner);

        showPreloader(profileContainer, { color: 'green', size: 'big' });

        try {
            const response = await fetchData(kontoJson, 'GET', false, false);
            var renderProfileData = function () {
                if (response.konto.angemeldet) {
                    // set button data
                    const buttonDefaults = {
                        title: response.konto.abmelden.label,
                        style: 'border', // default, border
                        tag: 'a',
                        link: response.konto.abmelden.link,
                        fullwidth: true,
                    };

                    // section
                    var html = `
                    <div class="navigation-main__flyout__section">
                        ${response.konto.begruessung.label}
                        <span class="flyout-user__name">
                            ${response.konto.begruessung.anrede}
                            ${response.konto.begruessung.vorname}
                            ${response.konto.begruessung.nachname}
                        </span>
                        ${response.konto.begruessung.kundennummer ? `
                            <span class="flyout-user__number">
                            ${response.konto.begruessung.kundennummerLabel} ${response.konto.begruessung.kundennummer}
                            </span>
                        ` : ''}
                    </div>
                    `;

                    // section
                    if (parseInt(response.konto.freizugeben.count) > 0) {
                        html += `
                        <div class="navigation-main__flyout__section">
                            <a class="flyout-user__notifications" href="${response.konto.freizugeben.link}">
                                <span class="flyout-user__notifications__number">
                                    ${response.konto.freizugeben.count}
                                </span>
                                ${response.konto.freizugeben.label}
                            </a>
                        </div>
                        `;
                    }
                    // section
                    html += `
                    <div class="navigation-main__flyout__section">
                    <div class="flyout-user__navigation">
                    `;
                    for (var i = 0; i < response.konto.linkliste.length; i++) {
                        html += `<a class="flyout-user__navigation__link" href="${response.konto.linkliste[i].link}">
                        ${response.konto.linkliste[i].label}
                        </a>`;
                    }
                    html += `</div></div>`;

                    // section
                    html += `
                    <div class="navigation-main__flyout__section navigation-main__flyout__buttons">
                        ${generateButton(buttonDefaults)}
                    </div>
                    `;
                } else {
                    const buttonDefaults = {
                        title: response.konto.login.label,
                        style: 'default', // default, border
                        tag: 'a',
                        link: response.konto.login.link,
                        fullwidth: true,
                    };
                    html = `



                    <div class="navigation-main__flyout__section">
                        ${generateButton(buttonDefaults)}
                    </div>
                    <div class="navigation-main__flyout__section flyout-user__registration-link-container">
                        <a href="${response.konto.registieren.link}" class="flyout-user__registration-link">
                            ${response.konto.registieren.label}
                        </a>
                    </div>
                    `;
                }

                // render
                profileInner.insertAdjacentHTML('beforeend', html);
            };

            // hide preloader
            hidePreloader(profileContainer);

            // render data
            renderProfileData();

            addMousePageLocker('profil');
        } catch (error) {
            profileInner.innerHTML = ''; // eslint-disable-line require-atomic-updates
            var renderProfileErrorData = function () {
                var html = `<div class="error-message">${errorMsg}</div>`;
                // render
                profileInner.insertAdjacentHTML('beforeend', html);
            };
            renderProfileErrorData();
        }
    }

    async function loadDashboard () {
        const dashboardContainer = element.querySelector(
            '.js-dashboard__container',
        );
        const dashboardInner = element.querySelector('.js-dashboard__inner');
        dashboardInner.innerHTML = '';
        showPreloader(dashboardContainer, { color: 'green', size: 'big' });

        try {
            const response = await fetchData(bestellJson, 'GET', false, false);

            var renderDashboardData = function () {
                // set button data
                const buttonDefaults = {
                    title: response.dashbutton.uebersicht.label
                        ? response.dashbutton.uebersicht.label
                        : null,
                    style: 'default', // default, border
                    tag: 'a',
                    link: response.dashbutton.uebersicht.link
                        ? response.dashbutton.uebersicht.link
                        : '#',
                    fullwidth: true,
                    class: response.dashbutton.uebersicht.link ? '' : 'disabled',
                };

                // section
                var html = `
                <div class="navigation-main__flyout__section flyout-dashboard__table__head flyout-dashboard__table__row">

                    <div class="flyout-dashboard__table__head-stuff">
                        ${response.dashbutton.labels.warenkorb}
                    </div>

                    <div class="flyout-dashboard__table__head-quantity">
                        ${response.dashbutton.labels.anzahl}
                    </div>

                    <div class="flyout-dashboard__table__head-summ">
                        ${response.dashbutton.labels.summe}
                    </div>
                </div>
            `;

                // section
                html += `
                <div class="flyout-dashboard__table__body">
                <div class="flyout-dashboard__table__body__scroll js-flyouts__scroll">
            `;
                if (response.dashbutton.produkte) {
                    for (
                        var i = 0;
                        i < response.dashbutton.produkte.length;
                        i++
                    ) {
                        html += `
                    <div class="navigation-main__flyout__section flyout-dashboard__table__item flyout-dashboard__table__row">

                        <div class="flyout-dashboard__table__col">
                            <div class="flyout-dashboard__table__item__description">
                                <div class="flyout-dashboard__table__item__image">
                                    <img class="lazyload" data-src="${response.dashbutton.produkte[i].bild.pfad}${response.dashbutton.produkte[i].bild.datei}" alt="${response.dashbutton.produkte[i].bezeichung}">
                                </div>

                                <div class="flyout-dashboard__table__item__name">
                                    ${response.dashbutton.produkte[i].bezeichung}
                                </div>
                            </div>



                            <div class="flyout-dashboard__table__item__quantity">
                                ${response.dashbutton.produkte[i].anzahl}
                            </div>
                        </div>


                        <div class="flyout-dashboard__table__item__summ">
                            ${response.dashbutton.produkte[i].preis.betrag}

                        </div>
                    </div>
                `;
                    }
                } else {
                    html += `<div class="error-message">${response.dashbutton.nachricht}</div>`;
                }
                html += `</div></div>`;

                // section
                html += `<div class="flyout-dashboard__table__foot">`;
                if (response.dashbutton.gesamtsumme) {
                    html += `<div class="flyout-dashboard__table__foot-top">
                            <div class="flyout-dashboard__table__foot__caption">
                                <span class="bold">
                                    ${response.dashbutton.gesamtsumme.label}
                                </span>
                                ${response.dashbutton.gesamtsumme.bruttoNetto}
                            </div>

                            <div class="flyout-dashboard__table__foot__summ">
                                ${response.dashbutton.gesamtsumme.betrag}

                            </div>
                        </div>`;
                }

                html += `<div class="navigation-main__flyout__buttons ${
                    response.dashbutton.gesamtsumme ? '' : 'no-border'
                }">
                            ${generateButton(buttonDefaults)}

                        </div>
                    </div>`;

                // render
                dashboardInner.insertAdjacentHTML('beforeend', html);
            };

            // hide preloader
            hidePreloader(dashboardContainer);

            // render
            renderDashboardData();

            // add scroll
            const scrollArea = dashboardInner.querySelector(
                '.js-flyouts__scroll',
            );
            addScrollBar(scrollArea);
            addMousePageLocker('dashboard');
        } catch (error) {
            console.log(error);
            dashboardInner.innerHTML = '';
            var renderDashboardErrorData = function () {
                var html = `<div class="error-message">${errorMsg}</div>`;
                // render
                dashboardInner.insertAdjacentHTML('beforeend', html);
            };

            renderDashboardErrorData();
        }
    }

    async function loadCart () {
        const cartContainer = element.querySelector('.js-cart__container');
        const cartInner = element.querySelector('.js-cart__inner');

        showPreloader(cartContainer, { color: 'green', size: 'big' });

        try {
            const response = await fetchData(
                warenkorbJson,
                'GET',
                false,
                false,
            );

            cartInner.innerHTML = '';
            var renderCartData = function () {
                // set button data
                const buttonLeftDefaults = {
                    title: response.warenkorb.kasse.label,
                    style: 'border', // default, border
                    tag: 'a',
                    link: response.warenkorb.kasse.link
                        ? response.warenkorb.kasse.link
                        : '#',
                    fullwidth: false,
                    class: response.warenkorb.kasse.link ? '' : 'disabled',
                };
                const buttonRightDefaults = {
                    title: response.warenkorb.warenkorb.label,
                    style: 'default', // default, border
                    tag: 'a',
                    link: response.warenkorb.warenkorb.link,
                    fullwidth: false,
                };

                // section
                var html = `
                <div class="navigation-main__flyout__section flyout-dashboard__table__head flyout-dashboard__table__row">

                    <div class="flyout-dashboard__table__head-stuff">
                        ${response.warenkorb.labels.warenkorb}
                    </div>

                    <div class="flyout-dashboard__table__head-quantity">
                        ${response.warenkorb.labels.anzahl}
                    </div>

                    <div class="flyout-dashboard__table__head-summ">
                        ${response.warenkorb.labels.summe}
                    </div>
                </div>
            `;

                // section
                html += `
                <div class="flyout-dashboard__table__body">
                <div class="flyout-dashboard__table__body__scroll js-flyouts__scroll">
            `;
                if (response.warenkorb.produkte) {
                    for (
                        var i = 0;
                        i < response.warenkorb.produkte.length;
                        i++
                    ) {
                        html += `
                        <div class="navigation-main__flyout__section flyout-dashboard__table__item flyout-dashboard__table__row">

                            <div class="flyout-dashboard__table__col">
                                <div class="flyout-dashboard__table__item__description">
                                    <div class="flyout-dashboard__table__item__image">
                                        <img class="lazyload" data-src="${response.warenkorb.produkte[i].bild.pfad}${response.warenkorb.produkte[i].bild.datei}" alt="${response.warenkorb.produkte[i].bezeichung}">
                                    </div>

                                    <div class="flyout-dashboard__table__item__name">
                                        ${response.warenkorb.produkte[i].bezeichung}
                                    </div>
                                </div>



                                <div class="flyout-dashboard__table__item__quantity">
                                    ${response.warenkorb.produkte[i].anzahl}
                                </div>
                            </div>


                            <div class="flyout-dashboard__table__item__summ">
                                ${response.warenkorb.produkte[i].preis.betrag}

                            </div>
                        </div>
                    `;
                    }
                } else {
                    html += `<div class="error-message">${response.warenkorb.nachricht}</div>`;
                }

                html += `</div></div>`;

                // section

                html += `<div class="flyout-dashboard__table__foot">`;
                if (response.warenkorb.gesamtsumme) {
                    html += `<div class="flyout-dashboard__table__foot-top">
                            <div class="flyout-dashboard__table__foot__caption">
                                <span class="bold">
                                    ${response.warenkorb.gesamtsumme.label}
                                </span>
                                ${response.warenkorb.gesamtsumme.bruttoNetto}
                            </div>

                            <div class="flyout-dashboard__table__foot__summ">
                                ${response.warenkorb.gesamtsumme.betrag}

                            </div>
                        </div>`;
                }

                html += `<div class="navigation-main__flyout__buttons ${
                    response.warenkorb.gesamtsumme ? '' : 'no-border'
                }">
                            ${generateButton(buttonLeftDefaults)}
                            ${generateButton(buttonRightDefaults)}
                        </div>
                    </div>`;

                // render
                cartInner.insertAdjacentHTML('beforeend', html);
            };
            // hide preloader
            hidePreloader(cartContainer);
            // render
            renderCartData();

            // add scroll
            const scrollArea = cartInner.querySelector('.js-flyouts__scroll');
            addScrollBar(scrollArea);
            addMousePageLocker('cart');
        } catch (error) {
            console.log(error);
            cartInner.innerHTML = '';
            var renderCartErrorData = function () {
                var html = `<div class="error-message">${errorMsg}</div>`;
                // render
                cartInner.insertAdjacentHTML('beforeend', html);
            };

            renderCartErrorData();
        }
    }

    function checkForLogout () {
        const urlParams = (new URL(window.location.href)).searchParams;
        const doParam = urlParams.get('do');
        if (doParam && (doParam === 'LOGOUT' || doParam === 'logout')) {
            logoutUser();
            return true;
        }
        return false;
    }

    function logoutUser () {
        loggedIn = false;
        loggedInData = false;
        Cookies.remove(loggedInCookieName);
    }

    async function generateMobileIcons (showDashboard) {
        // get dashboard link
        try {
            const response = await fetchData(bestellJson, 'GET', false, false);
            dashboardLink = response.dashbutton.uebersicht.link;
            console.log('mobile icons generate');

            const dashboardMobileHtml = `${showDashboard ? `
            <!-- dashboards -->
            <a href="${dashboardLink}" class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--dashboard">
                <div class="icon navigation-main__tool__icon icon--color">
                    ${generateIcon('dashboard', true)}
                </div>
                <div class="navigation-main__tool__label js-dashboard-label">
                </div>
                <div class="navigation-main__tool__notifications js-dashboard-notifications--mobile">
                </div>
            </a>
            ` : ''}`;

            // paste dashboard for mobile location
            swichesContainer.insertAdjacentHTML('beforeend', dashboardMobileHtml);
        } catch (error) {
            if (window.Sentry) {
                window.Sentry.captureException(error);
            }
            console.log(error);
        }

        // get cart link
        try {
            const response = await fetchData(warenkorbJson, 'GET', false, false);
            cartLink = response.warenkorb.warenkorb.link;

            const cartMobileHtml = `<!-- cart -->
            <a href="${cartLink}" class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--cart">

                    <div class="icon navigation-main__tool__icon icon--color">
                        ${generateIcon('cart', true)}
                    </div>

                    <div class="navigation-main__tool__label js-cart-label">
                    </div>
                    <div class="navigation-main__tool__notifications js-cart-notifications--mobile">
                    </div>
            </a>
            `;

            // paste cart for mobile location
            swichesContainer.insertAdjacentHTML('beforeend', cartMobileHtml);

            // load bubble data initially
            getInitialBubbleData();
        } catch (error) {
            if (window.Sentry) {
                window.Sentry.captureException(error);
            }
            console.log(error);
        }

        // re-init menu event listener for mobile
        swichesContainer
            .querySelectorAll('.js-header__toggle-flyout')
            .forEach(toggleFlyoutItem => {
                toggleFlyoutItem.addEventListener(
                    'click',
                    toggleFlyoutItemClickHandler,
                );
            });
    }

    function showLoginStatus (isLoggedIn) {
        let iconsHtml;
        let flyoutsHtml;
        let userMobileHtml;

        swichesContainer.innerHTML = '';
        loginContainer.innerHTML = '';
        flyoutsContainer.innerHTML = '';

        if (isLoggedIn) {
            const showDashboard = Object.keys(loggedInData).includes('dashbutton') && loggedInData.dashbutton.aktiviert;
            element.classList.add(states.isLoggedIn);
            setActiveSearchOption();

            // generate mobile icons
            generateMobileIcons(showDashboard);

            // generate mobile user icon
            userMobileHtml = `
                <!-- dropdown -->
                <div class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--user js-header__toggle-flyout" data-toggle-flyout="user">
                    <div class="icon navigation-main__tool__icon navigation-main__tool__open icon--color">
                        ${generateIcon('user', true)}
                    </div>
                    <div class="icon navigation-main__tool__icon navigation-main__tool__close icon--color">
                        ${generateIcon('close', true)}
                    </div>
                    <div class="navigation-main__tool__label js-user-label">
                    </div>
                    <div class="navigation-main__tool__notifications js-user-notifications--mobile">

                    </div>

                </div>`;

            // generate desktop icons
            iconsHtml = `
                <!-- dropdown -->
                <div class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--user js-header__toggle-flyout" data-toggle-flyout="user">
                    <div class="icon navigation-main__tool__icon icon--color">
                        ${generateIcon('user', true)}
                    </div>
                    <div class="navigation-main__tool__label js-user-label">
                    </div>
                    <div class="navigation-main__tool__notifications js-user-notifications">

                    </div>
                    <div class="icon navigation-main__tool__arrow icon--color">
                        ${generateIcon('chevron-down', true)}
                    </div>
                </div>
                ${showDashboard ? `
                <!-- dashboards -->
                <div class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--dashboard js-header__toggle-flyout" data-toggle-flyout="dashboard">
                    <div class="icon navigation-main__tool__icon icon--color">
                        ${generateIcon('dashboard', true)}
                    </div>
                    <div class="navigation-main__tool__label js-dashboard-label">
                    </div>
                    <div class="navigation-main__tool__notifications js-dashboard-notifications">
                    </div>
                    <div class="icon navigation-main__tool__arrow icon--color">
                        ${generateIcon('chevron-down', true)}
                    </div>
                </div>
                ` : ''}
                <!-- cart -->
                <div class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--cart js-header__toggle-flyout" data-toggle-flyout="cart">

                        <div class="icon navigation-main__tool__icon icon--color">
                            ${generateIcon('cart', true)}
                        </div>
                        <div class="navigation-main__tool__label js-cart-label">
                        </div>
                        <div class="navigation-main__tool__notifications js-cart-notifications">
                        </div>
                        <div class="icon navigation-main__tool__arrow icon--color">
                            ${generateIcon('chevron-down', true)}
                        </div>

                </div>
                `;
            flyoutsHtml = `
                    <!-- user -->
                    <div class="navigation-main__flyout flyout-user js-profil__container" data-flyout="user">
                        <div class="js-profil__inner">
                        </div>
                    </div>


                    <!-- dashboard -->
                    <div class="navigation-main__flyout navigation-main__flyout--wide flyout-dashboard js-dashboard__container"  data-flyout="dashboard">
                        <div class="js-dashboard__inner">
                        </div>
                    </div>

                    <!-- cart -->
                    <div class="navigation-main__flyout navigation-main__flyout--wide flyout-dashboard js-cart__container" data-flyout="cart">
                        <div class="js-cart__inner">
                        </div>
                    </div>
                `;
        } else {
            // generate mobile user icon
            console.log('genearate user icon');
            userMobileHtml = `
            <!-- dropdown -->
            <div class="navigation-main__tool navigation-main__tool--no-label-on-desktop navigation-main__tool--user js-header__toggle-flyout" data-toggle-flyout="user">
                <div class="icon navigation-main__tool__icon navigation-main__tool__open icon--color">
                    ${generateIcon('user', true)}
                </div>
                <div class="icon navigation-main__tool__icon navigation-main__tool__close icon--color">
                    ${generateIcon('close', true)}
                </div>
                <div class="navigation-main__tool__label js-user-label">
                </div>
                <div class="navigation-main__tool__notifications js-user-notifications--mobile">

                </div>

            </div>`;

            iconsHtml = `
                <div class="navigation-main__tool js-header__toggle-flyout" data-toggle-flyout="user">

                    <div class="icon navigation-main__tool__icon icon--color">
                        ${generateIcon('user', true)}
                    </div>

                    <div class="navigation-main__tool__label">
                        ${loginLabel}
                    </div>

                    <div class="icon navigation-main__tool__arrow icon--color">
                        ${generateIcon('chevron-down', true)}
                    </div>
                </div>
            `;
            flyoutsHtml = `
                <div class="navigation-main__flyout flyout-user js-profil__container" data-flyout="user">
                    <div class="js-profil__inner"></div>
                </div>
            `;
        }

        // paste icons for desktop location
        loginContainer.insertAdjacentHTML('beforeend', iconsHtml);
        flyoutsContainer.insertAdjacentHTML('beforeend', flyoutsHtml);

        // paste user for mobile location
        swichesContainer.insertAdjacentHTML('beforeend', userMobileHtml);

        // generate bubbles is inside generateMobileIcons as they are latest to finish

        // re-init menu event listener
        loginContainer
            .querySelectorAll('.js-header__toggle-flyout')
            .forEach(toggleFlyoutItem => {
                toggleFlyoutItem.addEventListener(
                    'click',
                    toggleFlyoutItemClickHandler,
                );
            });

        // re-init menu event listener for mobile
        swichesContainer
            .querySelectorAll('.js-header__toggle-flyout')
            .forEach(toggleFlyoutItem => {
                toggleFlyoutItem.addEventListener(
                    'click',
                    toggleFlyoutItemClickHandler,
                );
            });
    }

    async function checkLoginAsync () {
        try {
            const response = await fetchData(loginJson, 'GET', false, false);
            const loggedInResponse = response.konto.angemeldet;
            Cookies.set(loggedInCookieName, JSON.stringify(response), { expires: 1 });
            if (loggedInResponse !== loggedIn) {
                loggedInData = response;
                showLoginStatus(loggedInResponse);
            }
        } catch (error) {
            if (window.Sentry) {
                window.Sentry.captureException(error);
            }
            console.error(error);
        }
    }

    async function getInitialBubbleData () {
        try {
            const response = await fetchData(loginJson, 'GET', false, false);
            const loggedInResponse = response.konto.angemeldet;
            if (loggedInResponse) {
                updateBubble({
                    detail: {
                        type: 'profile',
                        count: response.konto.freizugeben,
                    },
                });
                updateBubble({
                    detail: {
                        type: 'dashboard',
                        count: response.dashbutton.anzahl,
                    },
                });
                updateBubble({
                    detail: {
                        type: 'cart',
                        count: response.warenkorb.anzahl,
                    },
                });
            }
        } catch (error) {
            if (window.Sentry) {
                window.Sentry.captureException(error);
            }
            console.error(error);
        }
    }

    async function updateBubble (e) {
        const type = e.detail.type; // data-type="profile, dashboard, cart"
        let bubble = null;
        let bubbleMobile = null;
        let count = null;

        // choosing the type
        switch (type) {
        case 'profile':
            bubble = element.querySelector('.js-user-notifications');
            bubbleMobile = element.querySelector('.js-user-notifications--mobile');
            break;
        case 'dashboard':
            bubble = element.querySelector('.js-dashboard-notifications');
            bubbleMobile = element.querySelector('.js-dashboard-notifications--mobile');
            break;
        case 'cart':
            bubble = element.querySelector('.js-cart-notifications');
            bubbleMobile = element.querySelector('.js-cart-notifications--mobile');
            break;

        default:
            break;
        }

        if (bubble !== null) {
            count = parseInt(e.detail.count);
            bubble.innerHTML = count;

            if (count && count > 0) {
                bubble.classList.add('has-notifications');
            } else {
                bubble.classList.remove('has-notifications');
            }
        }
        if (bubbleMobile !== null) {
            count = parseInt(e.detail.count);
            bubbleMobile.innerHTML = count;

            if (count && count > 0) {
                bubbleMobile.classList.add('has-notifications');
            } else {
                bubbleMobile.classList.remove('has-notifications');
            }
        }
    }

    function addScrollBar (scrollerEl) {
        scrollerEl.classList.remove('ss-container');
        scrollerEl.removeAttribute('style');
        const scrollerElHeight = scrollerEl.clientHeight;
        const vh = Math.max(
            document.documentElement.clientHeight,
            window.innerHeight || 0,
        );

        const isDesktop = store.getters.isDesktopNavigation;

        let maxHeight = 'none';

        if (isDesktop) {
            maxHeight = 278;
        } else {
            maxHeight = vh - 166 - 60 - 73;
        }

        let initializeScroller = false;
        if (maxHeight === 'none') {
            initializeScroller = true;
        } else if (scrollerElHeight >= maxHeight) {
            scrollerEl.style.height = maxHeight + 'px';
            initializeScroller = true;
        }
        if (initializeScroller) {
            SimpleScrollbar.initEl(scrollerEl);
        }
    }

    function addMousePageLocker (type) {
        // const flyoutElement = element.querySelector('.js-' + type + '__inner');
        // if (flyoutElement) {
        //     flyoutElement.addEventListener('mouseenter', lockPage);
        //     flyoutElement.addEventListener('mouseleave', unlockPage);
        // }
    }

    // SEARCH OPTIONS dropdown
    function toggleSearchOptionsFlyout () {
        elements.toggleSearchOptions.classList.toggle(states.isFlyoutOpen);
        store.commit('setSearchSuggestionsCurrentlyVisible', false);
    }

    function clickOutsideSearchOptionsFlyout (e) {
        const target = e.target;
        if (!target.classList.contains('js-header__search-options') && !target.closest(`.js-header__search-options`)) {
            elements.toggleSearchOptions.classList.remove(states.isFlyoutOpen);
        }
    }

    function updateSearchOptionSelection (e) {
        const target = e.currentTarget;
        const value = target.dataset.value;
        const label = target.innerHTML;
        searchOption = value;
        elements.toggleSearchOptionsSelected.innerHTML = label;
        elements.toggleSearchOptions.classList.remove(states.isFlyoutOpen);
        const storageData = { value: value, label: label };
        localStorage.setItem('recaSelectedSearchOption', JSON.stringify(storageData));
        dispatch('searchOptionUpdated', value);
        if (searchOption === 'all' && elements.searchInput.value !== '') {
            store.commit('setSearchSuggestionsCurrentlyVisible', true);
        }
    }

    function getLanguageLinksFromHreflang () {
        elements.languageLinks.forEach(link => {
            let hreflang = link.getAttribute('hreflang');
            if (hreflang) {
                let matchingLink = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
                // try selecting link with lang only ( example en-UK, select based on 'en' only )
                if (!matchingLink && hreflang.indexOf('-') > -1) {
                    hreflang = hreflang.split('-')[0];
                    matchingLink = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
                }
                if (matchingLink) {
                    const url = matchingLink.getAttribute('href');
                    link.setAttribute('href', url);
                }
            }
        });
    }

    init();
}
